import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuthStore } from '../stores/authStore'
import { shouldTrackVisits } from '../lib/visitor-tracking'

/*
 * VisitorTracker
 *
 *   Mounted ONCE inside the router. The capture layer for the Super-Admin
 *   Visitor Logs module: on every authenticated route change it POSTs a
 *   page_view to /visitor-logs/track, emits a session_start on the first
 *   event of a tab session, and a session_end (fetch keepalive) on unload.
 *
 *   - Auth uses the app's httpOnly cookie — no token handling here; the
 *     axios client sends credentials automatically, and the unload beacon
 *     uses fetch with `credentials: 'include'`.
 *   - Only ORGANISATION members are tracked (never the super admin) — the
 *     backend enforces this too, but gating here avoids the noise + the
 *     feedback loop where the super admin opening Visitor Logs logs itself.
 *   - Fire-and-forget: every call swallows errors so a logging hiccup can
 *     never affect navigation.
 *   - Session id lives in sessionStorage, so a "session" == a browser tab's
 *     lifetime — exactly what we want for journey analytics.
 */

const SESSION_KEY = 'taskmagix_vlog_session'

const ensureSession = () => {
  let id = sessionStorage.getItem(SESSION_KEY)
  if (!id) {
    id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
    sessionStorage.setItem(SESSION_KEY, id)
    return { id, isNew: true }
  }
  return { id, isNew: false }
}

/** Fire-and-forget POST via the axios client (cookie auth). */
const track = (payload) => {
  api.post('/visitor-logs/track', payload).catch(() => {})
}

/** Unload beacon — axios can't reliably fire during unload, so we use
 *  fetch with keepalive + credentials so the cookie rides along and the
 *  request survives the page teardown. */
const trackBeacon = (payload) => {
  try {
    const base = api.defaults.baseURL || ''
    fetch(`${base}/visitor-logs/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
      keepalive: true,
    }).catch(() => {})
  } catch {
    /* never throw from the tracker */
  }
}

export function VisitorTracker() {
  const location = useLocation()
  const status = useAuthStore((s) => s.status)
  const role = useAuthStore((s) => s.user?.role)
  const impersonation = useAuthStore((s) => s.impersonation)
  const sessionStartRef = useRef(null)
  const lastPathRef = useRef('')

  const enabled = shouldTrackVisits({ status, role, impersonation })

  // Page view on every route change.
  useEffect(() => {
    if (!enabled) return
    const { id, isNew } = ensureSession()
    const fullPath = location.pathname + (location.search || '')

    if (isNew) {
      sessionStartRef.current = Date.now()
      track({ sessionId: id, eventType: 'session_start', path: location.pathname })
    } else if (sessionStartRef.current == null) {
      // Existing tab session that survived a hard reload — approximate start.
      sessionStartRef.current = Date.now()
    }

    track({
      sessionId: id,
      eventType: 'page_view',
      path: fullPath,
      pageTitle: typeof document !== 'undefined' ? document.title : '',
      referrer: lastPathRef.current,
    })
    lastPathRef.current = fullPath
  }, [location.pathname, location.search, enabled])

  // Session end when the tab closes.
  useEffect(() => {
    const onUnload = () => {
      if (!enabled) return
      const id = sessionStorage.getItem(SESSION_KEY)
      if (!id) return
      trackBeacon({
        sessionId: id,
        eventType: 'session_end',
        path: lastPathRef.current,
        durationMs: sessionStartRef.current ? Date.now() - sessionStartRef.current : null,
      })
    }
    window.addEventListener('beforeunload', onUnload)
    return () => window.removeEventListener('beforeunload', onUnload)
  }, [enabled])

  return null
}

export default VisitorTracker
